import { mapLoadGridRows } from './loadGridRows';
import {
    applyListRowDeltaToQueryClient,
    collectLoadedIds,
} from '../../../utils/entityListRowCache';

const DELETED_STAGE_IDS = new Set([-1, -2]);

function isDeletedLoadStageId(stageId) {
    return DELETED_STAGE_IDS.has(Number(stageId));
}

/** Rows completed this session that may not be in the completedLoads infinite cache yet. */
export const PENDING_COMPLETED_LOADS_KEY = ['completedLoadsPendingInserts'];

export function rememberPendingCompletedLoad(qc, row) {
    const id = Number(row?.id ?? row?.loadDetailId);
    if (!qc || !Number.isFinite(id)) return;
    const mapped = mapLoadGridRows([row])[0] ?? row;
    qc.setQueryData(PENDING_COMPLETED_LOADS_KEY, (old) => {
        const list = Array.isArray(old) ? old : [];
        return [...list.filter((item) => Number(item?.id ?? item?.loadDetailId) !== id), mapped];
    });
}

export function forgetPendingCompletedLoad(qc, id) {
    const rowId = Number(id);
    if (!qc || !Number.isFinite(rowId)) return;
    qc.setQueryData(PENDING_COMPLETED_LOADS_KEY, (old) => {
        const list = Array.isArray(old) ? old : [];
        return list.filter((item) => Number(item?.id ?? item?.loadDetailId) !== rowId);
    });
}

export function isPendingCompletedLoad(qc, id) {
    const rowId = Number(id);
    if (!qc || !Number.isFinite(rowId)) return false;
    const list = qc.getQueryData(PENDING_COMPLETED_LOADS_KEY);
    if (!Array.isArray(list)) return false;
    return list.some((item) => Number(item?.id ?? item?.loadDetailId) === rowId);
}

function getLoadStageId(data) {
    const raw = data?.stageId ?? data?.stage?.id;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

function getLoadStageDisplayMode(data) {
    return String(
        data?.stage?.displayMode
        ?? data?.stage?.DisplayMode
        ?? data?.stageDisplayMode
        ?? data?.StageDisplayMode
        ?? ''
    )
        .trim()
        .toUpperCase();
}

/**
 * Which load grid a detail row belongs on.
 * Incomplete query is DisplayMode I; completed is C.
 * Soft-delete (StageId -1 or -2) belongs on the deleted grid.
 * Missing displayMode (list/detail DTOs often omit it) is 'unknown' — patch in place, do not remove.
 *
 * @returns {'incomplete' | 'completed' | 'deleted' | 'unknown' | null}
 */
export function getLoadListGridTarget(data) {
    if (data == null) return null;
    if (isDeletedLoadStageId(getLoadStageId(data))) return 'deleted';

    // FINAL / 2nd-weight (2) and post-trigger complete (4) are C even when DisplayMode is omitted or stale.
    const stageId = getLoadStageId(data);
    if (stageId === 2 || stageId === 4) return 'completed';

    const mode = getLoadStageDisplayMode(data);
    if (mode === 'C') return 'completed';
    if (mode === 'I') return 'incomplete';
    if (!mode) return 'unknown';
    return null;
}

/**
 * Stamp I/C onto a getLoadDetail payload when the DTO omits Stage.DisplayMode.
 * FINAL weigh/tare (updateStageId 2 or save StageDisplayMode C) belongs on completed.
 */
export function withLoadListDisplayMode(data, saveResult, updateStageId) {
    const row = data ?? {};
    const fromRow = String(row?.stage?.displayMode ?? row?.stageDisplayMode ?? '').trim().toUpperCase();
    const fromSave = String(saveResult?.stageDisplayMode ?? saveResult?.stage?.displayMode ?? '').trim().toUpperCase();
    let mode = fromRow || fromSave;
    const stageId = Number(row?.stageId ?? row?.stage?.id);
    if (
        fromSave === 'C'
        || Number(updateStageId) === 2
        || stageId === 2
        || stageId === 4
    ) {
        mode = 'C';
    } else if (fromRow === 'C') {
        mode = 'C';
    }
    if (!mode) return row;
    return {
        ...row,
        stageDisplayMode: mode,
        stage: {
            ...(row.stage ?? {}),
            id: row.stage?.id ?? row.stageId,
            displayMode: mode,
        },
    };
}

function removeFromLiveLoadGrids(qc, id) {
    forgetPendingCompletedLoad(qc, id);
    applyListRowDeltaToQueryClient(qc, 'incompleteLoads', { deleteIds: [id] });
    applyListRowDeltaToQueryClient(qc, 'completedLoads', { deleteIds: [id] });
}

function removeFromAllLoadGrids(qc, id) {
    removeFromLiveLoadGrids(qc, id);
    applyListRowDeltaToQueryClient(qc, 'deletedLoads', { deleteIds: [id] });
}

function patchLoadedLoadGridsInPlace(qc, id, row) {
    const rowId = Number(id);
    const delta = { askedIds: [rowId], refreshedRows: [row] };
    if (collectLoadedIds(qc, 'incompleteLoads').has(rowId)) {
        applyListRowDeltaToQueryClient(qc, 'incompleteLoads', delta);
    }
    if (collectLoadedIds(qc, 'completedLoads').has(rowId)) {
        applyListRowDeltaToQueryClient(qc, 'completedLoads', delta);
    }
}

/**
 * Apply a SignalR / getLoadDetail payload to incomplete and completed list caches.
 * Remove only for delete (crud D / stage -1) or an explicit non-I/C display mode.
 * Missing displayMode patches the row where it already is.
 */
export function applyLoadDetailToListCaches(qc, id, crudOp, data) {
    if (crudOp === 'D' || data == null) {
        removeFromAllLoadGrids(qc, id);
        return;
    }

    const resolved = withLoadListDisplayMode(data);
    const row = mapLoadGridRows([resolved])[0] ?? resolved;
    const target = getLoadListGridTarget(resolved);

    if (target === 'unknown') {
        if (isPendingCompletedLoad(qc, id)) {
            rememberPendingCompletedLoad(qc, { ...row, stage: { ...(row.stage ?? {}), displayMode: 'C' } });
            applyListRowDeltaToQueryClient(qc, 'incompleteLoads', { deleteIds: [id] });
            applyListRowDeltaToQueryClient(qc, 'completedLoads', {
                askedIds: [id],
                refreshedRows: [row],
                forceInsert: true,
            });
            return;
        }
        patchLoadedLoadGridsInPlace(qc, id, row);
        return;
    }

    if (target === 'completed') {
        rememberPendingCompletedLoad(qc, row);
        applyListRowDeltaToQueryClient(qc, 'incompleteLoads', { deleteIds: [id] });
        applyListRowDeltaToQueryClient(qc, 'deletedLoads', { deleteIds: [id] });
        applyListRowDeltaToQueryClient(qc, 'completedLoads', {
            askedIds: [id],
            refreshedRows: [row],
            forceInsert: true,
        });
        return;
    }

    if (target === 'incomplete') {
        // Tare/weigh writes several LOAD_DETAILS audits; an earlier I snapshot must not
        // pull a just-completed load back off the completed grid.
        if (isPendingCompletedLoad(qc, id)) return;
        forgetPendingCompletedLoad(qc, id);
        applyListRowDeltaToQueryClient(qc, 'completedLoads', { deleteIds: [id] });
        applyListRowDeltaToQueryClient(qc, 'deletedLoads', { deleteIds: [id] });
        applyListRowDeltaToQueryClient(qc, 'incompleteLoads', {
            askedIds: [id],
            refreshedRows: [row],
            forceInsert: true,
        });
        return;
    }

    if (target === 'deleted') {
        forgetPendingCompletedLoad(qc, id);
        applyListRowDeltaToQueryClient(qc, 'incompleteLoads', { deleteIds: [id] });
        applyListRowDeltaToQueryClient(qc, 'completedLoads', { deleteIds: [id] });
        applyListRowDeltaToQueryClient(qc, 'deletedLoads', {
            askedIds: [id],
            refreshedRows: [row],
            forceInsert: true,
        });
        return;
    }

    removeFromAllLoadGrids(qc, id);
}
